import { Address } from 'types/address.types'
import { AddressSearchInput } from 'components/smart/AddressSearchInput'
import Button from 'components/core/Button'
import Row from 'components/core/Row'
import { useEffect, useState } from 'react'
import { WIDGET_KEY } from '../widget.constants'
import { formatAddress } from 'utils/address.utils'
import Typography from 'components/core/Typography'
import Column from 'components/core/Column'
import { DatePicker } from 'components/composite/DatePicker'
import { formatDateTimeRangeString } from 'utils/date.utils'
import { showToastError } from 'hooks/useToast'
import { TRANSPORT_TYPE_OPTIONS } from './confirmEstimateDetailsWidget.constants'
import { MeasurementType, ServiceType, WeightType } from 'types/order.types'
import { SERVICE_TYPE_OPTIONS } from 'constants/order.constants'
import { ServiceTypeSelect } from 'components/composite/ServiceTypeSelect'
import Select from 'components/core/Select'
import { WidgetComponentProps } from '../../wilsonChat.types'
import NumberInput from 'components/composite/NumberInput'
import { AccessorialSelect } from 'components/composite/AccessorialSelect'
import { AccessorialType } from 'types/accessorial.types'
import { MeasurementsTypeSelect } from 'components/composite/MeasurementsTypeSelect'
import { ConfirmShipmentDetailsWidgetCommodity } from '../ConfirmShipmentDetailsWidget/components/ConfirmShipmentDetailsWidgetCommodity'
import { CommodityFreightType } from 'types/orderCommodity.types'
import { useIsMobile } from 'hooks/useIsMobile'
import { getDefaultCommodity } from '../ConfirmShipmentDetailsWidget/confirmShipmentDetailsWidget.utils'
import { useMe } from 'hooks/useMe'
import { ACCESSORIAL_OPTIONS } from 'constants/accessorial.constants'
import { getCommoditySizeString } from 'utils/commodity.utils'
import { DESIGN_TOKENS } from 'constants/color.constants'

type Commodity = {
    description?: string | null
    quantity?: number | null
    freightType?: CommodityFreightType | null
    width?: number | null
    height?: number | null
    weight?: number | null
    length?: number | null
    numOfUnits?: number | null
}

export type ConfirmEstimateDetailsWidgetData = {
    status?: 'confirmed' | 'cancelled'
    pickupDateTime?: string | null
    pickupLocation?: Address | null
    dropoffLocation?: Address | null
    transportType?: 'dry-van' | 'reefer' | 'flatbed' | 'super-b' | null
    serviceType?: ServiceType | null
    weight?: number | null
    accessorials?: AccessorialType[] | null
    commodities?: Commodity[] | null
    weightType?: WeightType | null
    measurementType?: MeasurementType | null
}

export type ConfirmEstimateDetailsWidgetProps = WidgetComponentProps<
    'ConfirmEstimateDetails',
    ConfirmEstimateDetailsWidgetData
>

export type ConfirmEstimateDetailsCommodityError = {
    description?: string
    weight?: string
    quantity?: string
    len?: string
    width?: string
    height?: string
}

export type ConfirmEstimateDetailsWidgetErrors = {
    pickupLocation?: string
    dropoffLocation?: string
    pickupDateTime?: string
    transportType?: string
    serviceType?: string
    weight?: number
    commodities?: ConfirmEstimateDetailsCommodityError[]
}

export const ConfirmEstimateDetailsWidget = ({
    messageId,
    widgetData,
    onSendMessage,
}: ConfirmEstimateDetailsWidgetProps) => {
    const [mutation, setMutation] = useState(widgetData)
    const [errors, setErrors] = useState<ConfirmEstimateDetailsWidgetErrors>({})
    const isMobile = useIsMobile()
    const { me } = useMe({ refetchOnMount: false })
    const isLtl = mutation.serviceType === 'ltl'

    const _onValidate = (data: Partial<typeof mutation>) => {
        const errors: ConfirmEstimateDetailsWidgetErrors = {}

        if (
            !data.pickupLocation?.placeId &&
            !data.pickupLocation?.addressBookId
        ) {
            errors.pickupLocation = 'Pickup location is required'
        }

        if (
            !data.dropoffLocation?.placeId &&
            !data.dropoffLocation?.addressBookId
        ) {
            errors.dropoffLocation = 'Delivery location is required'
        }

        if (isLtl) {
            const commoditiesErrorsArr: ConfirmEstimateDetailsCommodityError[] =
                []

            data.commodities?.forEach((commodity) => {
                const commodityErrorsMap: ConfirmEstimateDetailsCommodityError =
                    {}

                if (!commodity.description) {
                    commodityErrorsMap.description = 'Description is required'
                }

                if (!commodity.weight) {
                    commodityErrorsMap.weight = 'Weight is required'
                }

                if (!commodity.quantity) {
                    commodityErrorsMap.quantity = 'Quantity is required'
                }

                if (!commodity.length) {
                    commodityErrorsMap.len = 'Length is required'
                }

                if (!commodity.width) {
                    commodityErrorsMap.width = 'Width is required'
                }

                if (!commodity.height) {
                    commodityErrorsMap.height = 'Height is required'
                }

                if (Object.keys(commodityErrorsMap).length) {
                    commoditiesErrorsArr.push(commodityErrorsMap)
                }
            })

            if (commoditiesErrorsArr.length) {
                errors.commodities = commoditiesErrorsArr
            }
        } else {
            if (!data.transportType) {
                errors.transportType = 'Transport type is required'
            }

            if (!data.serviceType) {
                errors.serviceType = 'Service type is required'
            }
        }

        if (!Object.keys(errors).length) {
            setErrors({})
            return
        }

        setErrors(errors)
        return errors
    }

    const _onGetEstimate = () => {
        const errors = _onValidate(mutation)
        if (errors) {
            showToastError('Please fill in all required fields')
            return
        }
        onSendMessage({
            message: '[Action: Get Estimate]',
            payload: {
                replyToWidget: WIDGET_KEY.confirmEstimateDetails,
                replyToMessageId: messageId,
                widgetData: { status: 'confirmed', ...mutation },
            },
        })
    }

    const _onCancel = () => {
        onSendMessage({
            message: '[Action: Cancel]',
            payload: {
                replyToWidget: WIDGET_KEY.confirmEstimateDetails,
                replyToMessageId: messageId,
                widgetData: { status: 'cancelled', ...mutation },
            },
        })
    }

    useEffect(() => {
        if (isLtl && !mutation.commodities?.length) {
            setMutation((prev) => ({
                ...prev,
                commodities: [getDefaultCommodity(widgetData)],
            }))
        }
        if (!isLtl) {
            setErrors((prev) => {
                return { ...prev, commodities: [] }
            })
        }
    }, [isLtl, mutation.commodities?.length, widgetData])

    if (widgetData.status) {
        const pickupLocation = formatAddress(mutation.pickupLocation ?? {}, {
            showName: !!mutation.pickupLocation?.addressBookId,
        })
        const deliveryLocation = formatAddress(mutation.dropoffLocation ?? {}, {
            showName: !!mutation.dropoffLocation?.addressBookId,
        })
        const pickupDate = formatDateTimeRangeString({
            from: mutation.pickupDateTime,
        })
        const transportType = TRANSPORT_TYPE_OPTIONS.find(
            (option) => option.value === mutation.transportType
        )
        const serviceType = SERVICE_TYPE_OPTIONS.find(
            (option) => option.value === mutation.serviceType
        )
        const commodities =
            mutation.commodities?.map((c) => ({
                quantityStr: [c.freightType, c.quantity ?? 1]
                    .filter(Boolean)
                    .join(' x '),
                descriptionStr: [c.description ?? 'N/A', c.numOfUnits]
                    .filter(Boolean)
                    .join(' x '),
                dimensionsStr: getCommoditySizeString(c, {
                    measurementType: mutation.measurementType,
                }),
                weightStr: [c.weight, mutation?.weightType ?? 'lbs']
                    .filter(Boolean)
                    .join(' '),
            })) ?? []

        return (
            <Column
                gap="p1"
                p="p2"
                br="sm"
                bgColor={DESIGN_TOKENS.background.secondary}
            >
                {pickupLocation && (
                    <Column>
                        <Typography isBold variant="body2" color="primary">
                            Pickup location:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {pickupLocation}
                        </Typography>
                    </Column>
                )}
                {deliveryLocation && (
                    <Column>
                        <Typography isBold variant="body2" color="primary">
                            Delivery location:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {deliveryLocation}
                        </Typography>
                    </Column>
                )}
                {serviceType && (
                    <Column>
                        <Typography isBold variant="body2" color="primary">
                            Service type:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {serviceType.label}
                        </Typography>
                    </Column>
                )}
                {isLtl ? (
                    <>
                        {!!mutation.accessorials?.length && (
                            <Column>
                                <Typography
                                    isBold
                                    variant="body2"
                                    color="primary"
                                >
                                    Accessorials:
                                </Typography>
                                <Typography variant="body2" color="primary">
                                    {mutation.accessorials
                                        ?.map(
                                            (a) =>
                                                ACCESSORIAL_OPTIONS.find(
                                                    (o) => o.value === a
                                                )?.label
                                        )
                                        .join(', ') || '--'}
                                </Typography>
                            </Column>
                        )}
                        {!!commodities.length && (
                            <Column>
                                <Typography
                                    isBold
                                    variant="body2"
                                    color="primary"
                                >
                                    Commodities:
                                </Typography>
                                {commodities.map((c, index) => (
                                    <Column key={index}>
                                        <Typography
                                            variant="body2"
                                            color="primary"
                                        >
                                            -{' '}
                                            {[
                                                c.descriptionStr,
                                                c.quantityStr,
                                                c.dimensionsStr,
                                                c.weightStr,
                                            ].join(', ')}
                                        </Typography>
                                    </Column>
                                ))}
                            </Column>
                        )}
                    </>
                ) : (
                    <>
                        {pickupDate && (
                            <Column>
                                <Typography
                                    isBold
                                    variant="body2"
                                    color="primary"
                                >
                                    Pickup date:
                                </Typography>
                                <Typography variant="body2" color="primary">
                                    {pickupDate}
                                </Typography>
                            </Column>
                        )}
                        {transportType && (
                            <Column>
                                <Typography
                                    isBold
                                    variant="body2"
                                    color="primary"
                                >
                                    Transport type:
                                </Typography>
                                <Typography variant="body2" color="primary">
                                    {transportType.label}
                                </Typography>
                            </Column>
                        )}
                        {mutation.weight && (
                            <Column>
                                <Typography
                                    isBold
                                    variant="body2"
                                    color="primary"
                                >
                                    Weight:
                                </Typography>
                                <Typography variant="body2" color="primary">
                                    {mutation.weight} lbs
                                </Typography>
                            </Column>
                        )}
                    </>
                )}
                <Row justifyContent="flex-end">
                    <Typography isItalic variant="body2" color="secondary">
                        {widgetData.status === 'confirmed'
                            ? 'Confirmed'
                            : 'Cancelled'}
                    </Typography>
                </Row>
            </Column>
        )
    }

    return (
        <Column
            gap="p1"
            p="p2"
            br="sm"
            bgColor={DESIGN_TOKENS.background.secondary}
        >
            <AddressSearchInput
                isRequiredLabel
                hasAddressBookSearch
                customerId={me.customerId}
                label="Pickup location"
                placeholder="Search pickup"
                error={errors.pickupLocation}
                addressBookId={mutation.pickupLocation?.addressBookId}
                value={mutation.pickupLocation?.placeId ?? ''}
                onChange={(address) =>
                    setMutation({
                        ...mutation,
                        pickupLocation: address,
                    })
                }
            />
            <AddressSearchInput
                isRequiredLabel
                hasAddressBookSearch
                customerId={me.customerId}
                label="Delivery location"
                placeholder="Search delivery"
                error={errors.dropoffLocation}
                addressBookId={mutation.dropoffLocation?.addressBookId}
                value={mutation.dropoffLocation?.placeId ?? ''}
                onChange={(address) =>
                    setMutation({
                        ...mutation,
                        dropoffLocation: address,
                    })
                }
            />
            <ServiceTypeSelect
                isRequiredLabel
                menuPlacement="top"
                label="Service type"
                error={errors.serviceType}
                value={mutation.serviceType ?? undefined}
                onChange={(serviceType) =>
                    setMutation({
                        ...mutation,
                        serviceType,
                    })
                }
            />
            {isLtl ? (
                <>
                    <AccessorialSelect
                        label="Accessorials"
                        placeholder="Accessorials"
                        menuPlacement="top"
                        accessorials={mutation.accessorials ?? []}
                        onChange={(accessorials) =>
                            setMutation({ ...mutation, accessorials })
                        }
                    />
                    <MeasurementsTypeSelect
                        label="Measurements"
                        weightType={mutation.weightType ?? 'lbs'}
                        measurementType={mutation.measurementType ?? 'in'}
                        onChange={(weightType, measurementType) =>
                            setMutation({
                                ...mutation,
                                weightType,
                                measurementType,
                            })
                        }
                    />
                    {mutation.commodities?.map((commodity, fieldIndex) => {
                        const commodityId = `commodity-${fieldIndex}`
                        const commodityErrors =
                            errors.commodities?.[fieldIndex] ?? {}

                        return (
                            <ConfirmShipmentDetailsWidgetCommodity
                                key={commodityId}
                                isMobile={isMobile}
                                hideDeleteButton={
                                    isLtl &&
                                    !!mutation.commodities?.length &&
                                    mutation.commodities.length === 1
                                }
                                isRequiredLabel={!!isLtl}
                                label={`Commodity ${fieldIndex + 1}`}
                                commodity={commodity}
                                commodityErrors={commodityErrors}
                                measurementType={
                                    mutation.measurementType ?? 'in'
                                }
                                weightType={mutation.weightType ?? 'lbs'}
                                onDuplicate={() => {
                                    setMutation((prev) => ({
                                        ...prev,
                                        commodities: [
                                            ...(prev.commodities ?? []),
                                            commodity,
                                        ],
                                    }))
                                }}
                                onChange={(newCommodity) => {
                                    setMutation({
                                        ...mutation,
                                        commodities: mutation.commodities?.map(
                                            (oldCommodity, index) =>
                                                index === fieldIndex
                                                    ? newCommodity
                                                    : oldCommodity
                                        ),
                                    })
                                }}
                                onRemove={() =>
                                    setMutation((prev) => ({
                                        ...prev,
                                        commodities: (
                                            prev.commodities ?? []
                                        ).filter(
                                            (_, index) => index !== fieldIndex
                                        ),
                                    }))
                                }
                            />
                        )
                    })}
                </>
            ) : (
                <>
                    <DatePicker
                        isRequiredLabel
                        isEditable
                        hasTime={false}
                        label="Pickup date"
                        error={errors.pickupDateTime}
                        placeholder="Pickup date"
                        value={mutation.pickupDateTime}
                        onChange={(pickupDateTime) =>
                            setMutation({
                                ...mutation,
                                pickupDateTime,
                            })
                        }
                    />
                    {/* These options are specific to Greenscreens
                     * Not creating a separate component since we're
                     * not going to be using it anywhere else for now.
                     **/}
                    <Select
                        isRequiredLabel
                        menuPlacement="top"
                        label="Transport type"
                        error={errors.transportType}
                        value={mutation.transportType ?? undefined}
                        options={TRANSPORT_TYPE_OPTIONS}
                        onSelectedOptionChanged={(transportType) =>
                            setMutation({
                                ...mutation,
                                transportType:
                                    transportType as ConfirmEstimateDetailsWidgetData['transportType'],
                            })
                        }
                    />
                    <NumberInput
                        value={mutation.weight ?? undefined}
                        label="Weight"
                        placeholder="Weight (lbs)"
                        suffix={' lbs'}
                        onChange={(weight) =>
                            setMutation({
                                ...mutation,
                                weight,
                            })
                        }
                    />
                </>
            )}
            {!widgetData.status && (
                <Row gap="p1" justifyContent="flex-end">
                    <Button variant="secondary" onClick={_onCancel}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={_onGetEstimate}>
                        Get Estimate
                    </Button>
                </Row>
            )}
        </Column>
    )
}
